<template>
  <div class="split-panel-item" v-press="() => emit('select')">
    <div v-if="category" class="category-tile" :style="categoryStyle">
      <h3 class="item-title">{{ categoryLabel }}</h3>
    </div>

    <template v-else>
      <TG4Image class="item-image" :image="image" aspect="landscape_fill" :alt="itemTitle" />
      <div class="item-overlay">
        <div class="item-text">
          <h3 class="item-title">{{ itemTitle }}</h3>
          <p class="item-subtitle" v-if="itemSubtitle">{{ itemSubtitle }}</p>
        </div>
      </div>
      <TG4PlayIcon class="play-icon" />
    </template>
  </div>
</template>

<script setup lang="ts">

import { computed } from 'vue';
import { RailItemResponse } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { VideoFormatter } from '@/helpers/video-formatter';
import TG4Image from '@/components/TG4Image.vue';
import TG4PlayIcon from '@/components/TG4PlayIcon.vue';

const props = defineProps<{
  item: RailItemResponse;
}>();

const emit = defineEmits<{
  (e: 'select'): void
}>();

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const isIrish = computed(() => currentLanguage.value === Language.Irish);

const category = computed(() => (props.item.type === 'CATEGORY' ? props.item.category : null));

const categoryLabel = computed(() => {
  const value = category.value;
  if (!value) return '';
  return (isIrish.value ? value.displayGa : value.displayEn) ?? value.displayEn ?? '';
});

// Pink when the CMS has not picked a colour, matching the other platforms.
const categoryStyle = computed(() => ({
  'background-color': category.value?.color ?? undefined,
}));

const image = computed(() => {
  const item = props.item;
  if (item.type === 'SERIES') return item.series.mainImage;
  if (item.type === 'VIDEO') return item.video.image;
  return null;
});


const itemSubtitle = computed(() => {
  const item = props.item;
  if (item.type === 'SERIES') return "";
  if (item.type === 'VIDEO') return item.video.seriesTitle ?? item.video.displayTitle ?? '';
  return '';
});

const itemTitle = computed(() => {
  const item = props.item;
  if (item.type === 'SERIES') return item.series.name;
  if (item.type === 'VIDEO') return VideoFormatter.getEpisodeCellSubtitle(item.video) ?? '';
  return '';
});
</script>

<style scoped lang="scss">
.split-panel-item {
  position: relative;
  height: 100%;
  outline: 0;

  // Drawn over the content rather than around it, so focus never insets the image.
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border: $focus-border-width solid transparent;
    box-sizing: border-box;
    pointer-events: none;
  }

  &:focus::after {
    border-color: $color-pink;
  }
}

.category-tile {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 30px;
  background-color: $color-pink;
}

.item-image {
  height: 100%;
}

.item-overlay {
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding-left: 40px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0));
}

.play-icon {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 90px;
  height: 90px;
}

.item-text {
  min-width: 0;
  text-align: left;
}

.item-title {
  margin: 0;
  @include text-cell-title;
  @include text-white;
  @include truncate;
  font-size: $text-subtitle;
}

.item-subtitle {
  margin: 0;
  @include text-cell-subtitle;
  @include text-white;
  @include truncate;
  font-size: $text-subtitle;
}
</style>
